import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { get, post, put } from "../../../../../helpers/api_helper";
import Select from "react-select";
import debounce from "lodash/debounce";

export const PLANETS = [
  { label: "Earth", value: "earth" },
  { label: "Moon", value: "moon" },
  { label: "Mercury", value: "mercury" },
  { label: "Venus", value: "venus" },
  { label: "Mars", value: "mars" },
  { label: "Jupiter", value: "jupiter" },
  { label: "Saturn", value: "saturn" },
  { label: "Uranus", value: "uranus" },
  { label: "Neptune", value: "neptune" },
  { label: "Pluto", value: "pluto" }
];

export const ASPECTS = [
  { label: "Conjunction (0°)", value: 0 },
  { label: "Semisextile (30°)", value: 30 },
  { label: "Semiquintile (36°)", value: 36 },
  { label: "Semi-Square (45°)", value: 45 },
  { label: "Sextile (60°)", value: 60 },
  { label: "Quintile (72°)", value: 72 },
  { label: "Square (90°)", value: 90 },
  { label: "Trine (120°)", value: 120 },
  { label: "Inconjunct (150°)", value: 150 },
  { label: "Sesqui-Quadrate (135°)", value: 135 },
  { label: "Biquintile (144°)", value: 144 },
  { label: "Inconjunct (150°)", value: 150 },
  { label: "Opposition (180°)", value: 180 },
  { label: "Gann 225°", value: 225 },
  { label: "No Aspect", value: 999 }
];

const AddHelioConfig = ({ showModal, setShowModal, mode, data, refresh }) => {
  const [stocks, setStocks] = useState([]);
  const [loadingStocks, setLoadingStocks] = useState(false);
  const formik = useFormik({
    initialValues: {
      masterPlanet: "",
      secondaryPlanet: "",
      aspectDegree: "",
      direction: "up",
      status: "active",
      stockId: "",
    },
    onSubmit: async (values) => {
      try {
        if (mode === "create") {
          await post("/admin/heliocentric-config", values);
          toast.success("Config created successfully");
        } else {
          await put(`/admin/heliocentric-config/${data._id}`, values);
          toast.success("Config updated successfully");
        }
        setShowModal(false);
        refresh();
      } catch (err) {
        toast.error("Error");
      }
    },
  });


    const fetchStocks = useCallback(async (search = "") => {
    try {
      setLoadingStocks(true);

      const skip = 0;
      const itemsPerPage = 10;

      const response = await get(
        `/admin/stock-exchange?skip=${skip}&limit=${itemsPerPage}&search=${search}`
      );

      if (response?.success) {
        setStocks(response?.data?.items || []);
      } else {
        setStocks([]);
      }
    } catch (error) {
      console.error("Error fetching stocks", error);
      setStocks([]);
    } finally {
      setLoadingStocks(false);
    }
  }, []);


  useEffect(() => {
    fetchStocks();
  }, [fetchStocks]);

    const debouncedFetchStocks = useCallback(
      debounce((searchText) => {
        fetchStocks(searchText);
      }, 1000),
      [fetchStocks]
    );

  useEffect(() => {
    if (mode === "edit" && data) {
      formik.setValues({
        stockId: data.stockId || "",
        masterPlanet: data.masterPlanet || "",
        secondaryPlanet: data.secondaryPlanet || "",
        aspectDegree: data.aspectDegree || null,
        direction: data.direction || "up",
        status: data.status || "active",
      });
    }
    if (mode === "create") {
      formik.resetForm();
    }
  }, [mode, data]);

  const stockOptions = stocks.map((stock) => ({
    value: stock._id,
    label: stock.ticker_name,
  }));

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} centered>
      <Modal.Body>
        <h5>{mode === "edit" ? "Edit Config" : "Add Config"}</h5>

        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Master Planet</Form.Label>
                <Form.Select
                name="masterPlanet"
                value={formik.values.masterPlanet}
                onChange={formik.handleChange}
                >
                <option value="">Select Master Planet</option>
                {PLANETS.map((p) => (
                    <option key={p.value} value={p.value}>
                    {p.label}
                    </option>
                ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Secondary Planet</Form.Label>
                <Form.Select
                name="secondaryPlanet"
                value={formik.values.secondaryPlanet}
                onChange={formik.handleChange}
                >
                <option value="">Select Secondary Planet</option>
                {PLANETS.map((p) => (
                    <option key={p.value} value={p.value}>
                    {p.label}
                    </option>
                ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Aspect Degree</Form.Label>
                <Form.Select
                name="aspectDegree"
                value={formik.values.aspectDegree}
                onChange={formik.handleChange}
                >
                <option value="">Select Aspect</option>
                {ASPECTS.map((a) => (
                    <option key={a.value} value={a.value}>
                    {a.label}
                    </option>
                ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Direction</Form.Label>
                <Form.Select
                  name="direction"
                  value={formik.values.direction}
                  onChange={formik.handleChange}
                >
                  <option value="up">Up</option>
                  <option value="down">Down</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <label className="form-label text-dark m-0 pb-1">
                Stock
              </label>

            <Select
              options={stockOptions}
              isLoading={loadingStocks}
              value={
                stockOptions.find(
                  (opt) => opt.value === formik.values.stockId
                ) || null
              }
              onChange={(selectedOption) => {
                formik.setFieldValue(
                  "stockId",
                  selectedOption ? selectedOption.value : ""
                );
              }}
              onInputChange={(inputValue, actionMeta) => {
                // prevent unnecessary API call on select
                if (actionMeta.action === "input-change") {
                  debouncedFetchStocks(inputValue);
                }
              }}
              placeholder="Search or select stock..."
              isClearable
              filterOption={null}
            />

              {formik.touched.stockId && formik.errors.stockId && (
                <div className="text-danger">{formik.errors.stockId}</div>
              )}
            </Col>

            <Col md={12} className="mt-3">
              <Button type="submit" className="w-100">
                {mode === "create" ? "Add" : "Update"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddHelioConfig;